import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { Card, CardHeader, Switch, CheckBox } from '@ui5/webcomponents-react';
import { Grid, TextField } from "@mui/material"; // Import TextField from MUI
import httpRequest from "../../httpRequest";
import ButtonContained from "../StyledComponents/ButtonContained";
import ButtonOutlined from "../StyledComponents/ButtonOutlined";

const SmtpCard = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [smtpEnabled, setSmtpEnabled] = useState(false);
  const [server, setServer] = useState("");
  const [port, setPort] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fromName, setFromName] = useState("");
  const [useTls, setUseTls] = useState(false);
  const [useSsl, setUseSsl] = useState(false);
  const [invoiceWorkflowNotifications, setInvoiceWorkflowNotifications] = useState(false);
  const [expenseWorkflowNotifications, setExpenseWorkflowNotifications] = useState(false);
  const [userOnboardingNotifications, setUserOnboardingNotifications] = useState(false);
  const [vendorCommunicationNotifications, setVendorCommunicationNotifications] = useState(false);
  const [testEmail, setTestEmail] = useState(""); // New state for the test email
  const [emailSubject, setEmailSubject] = useState("Test Email"); 
  const [emailMessage, setEmailMessage] = useState("This is a test email sent from my SmartIDP Software.");


  const [serverError, setServerError] = useState(false);
  const [portError, setPortError] = useState(false);
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [fromNameError, setFromNameError] = useState(false);
  const [testEmailError, setTestEmailError] = useState(false);
  const [emailSubjectError, setEmailSubjectError] = useState(false);
  const [emailMessageError, setEmailMessageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [smtpExists, setSmtpExists] = useState(false); // Track if SMTP settings exist



  const fetchSmtpSettings = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/global-smtp-settings`);
      setSmtpEnabled(response.data.smtp_enabled);
      setServer(response.data.server);
      setPort(response.data.port);
      setUsername(response.data.username);
      setPassword(response.data.password);
      setFromName(response.data.from_name);
      setUseTls(response.data.use_tls);
      setUseSsl(response.data.use_ssl);
      setInvoiceWorkflowNotifications(response.data.invoice_workflow_notifications);
      setExpenseWorkflowNotifications(response.data.expense_workflow_notifications);
      setUserOnboardingNotifications(response.data.user_onboarding_notifications);
      setVendorCommunicationNotifications(response.data.vendor_communication_notifications);
      setSmtpExists(true);
    } catch (error) {
      console.log(error)
      setSmtpExists(false);
    }
  };

  const toggleSmtpEnabled = async (enabled) => {
    setSmtpEnabled(enabled);
    if (!enabled) {
      try {
        await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/global-smtp-settings`, { smtp_enabled: false });
        enqueueSnackbar("SMTP disabled successfully", { variant: "success" });
      } catch (error) {
 console.log(error);
      }
    }
  };

  const testSmtpSettings = async () => {
    if (!smtpEnabled) {
      enqueueSnackbar("Please enable SMTP first!", { variant: "warning" });
      return;
    }

    // Validate that all required fields are filled out
    if (!server || !port || !username || !password || !fromName || !testEmail || !emailSubject || !emailMessage) {
        enqueueSnackbar("Please fill in all required fields", { variant: "warning" });
         // Set error state for the empty fields
         if (!server) setServerError(true);
         if (!port) setPortError(true);
         if (!username) setUsernameError(true);
         if (!password) setPasswordError(true);
         if (!fromName) setFromNameError(true);
         if (!testEmail) setTestEmailError(true);
         if (!emailSubject) setEmailSubjectError(true);
         if (!emailMessage) setEmailMessageError(true);
        return;
    }

    try {
        setServerError(false);
        setPortError(false);
        setUsernameError(false);
        setPasswordError(false);
        setFromNameError(false);
        setTestEmailError(false);
        setEmailSubjectError(false);
        setEmailMessageError(false);

        const response = await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/test-smtp-settings`, {
            server,
            port,
            username,
            password,
            from_name: fromName,
            use_tls: useTls,
            use_ssl: useSsl,
            to_email: testEmail,
            subject: emailSubject,
            message: emailMessage
        });

        // Check response to ensure success
        if (response.data.success) {
            enqueueSnackbar("Test email sent successfully", { variant: "success" });
        } else {
            enqueueSnackbar("Failed to send test email: " + response.data.error, { variant: "error" });
        }
    } catch (error) {
        // Log error for debugging purposes
        console.error("Error sending test email:", error);
        
        // Provide user feedback based on the error response
        const errorMessage = error.response?.data?.error || "Failed to send test email. Please check your SMTP settings.";
        enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  const saveSmtpSettings = async () => {
    if (!server || !port || !username || !password || !fromName) {
      enqueueSnackbar("Please fill in all required fields", { variant: "warning" });
      if (!server) setServerError(true);
      if (!port) setPortError(true);
      if (!username) setUsernameError(true);
      if (!password) setPasswordError(true);
      if (!fromName) setFromNameError(true);
      return;
    }

    try {
      setServerError(false);
      setPortError(false);
      setUsernameError(false);
      setPasswordError(false);
      setFromNameError(false);
      setTestEmailError(false);
      setEmailSubjectError(false);
      setEmailMessageError(false);
      
      const requestPayload = {
        smtp_enabled: smtpEnabled,
        server,
        port,
        username,
        password,
        from_name: fromName,
        use_tls: useTls,
        use_ssl: useSsl,
        invoice_workflow_notifications: invoiceWorkflowNotifications,
        expense_workflow_notifications: expenseWorkflowNotifications,
        user_onboarding_notifications: userOnboardingNotifications,
        vendor_communication_notifications: vendorCommunicationNotifications
      };

      // If SMTP settings exist, update using PUT, otherwise create using POST
      if (smtpExists) {
        await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/global-smtp-settings`, requestPayload);
        enqueueSnackbar("SMTP settings updated successfully", { variant: "success" });
      } else {
        await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/global-smtp-settings`, requestPayload);
        enqueueSnackbar("SMTP settings saved successfully", { variant: "success" });
        setSmtpExists(true); // Set to true after the first save
      }
    } catch (error) {
      enqueueSnackbar("Failed to save SMTP settings", { variant: "error" });
    }
  };

  useEffect(() => {
    fetchSmtpSettings();
  }, []);

  useEffect(() => {
    fetchSmtpSettings().then(() => setIsLoading(false));
  }, []);
  

  return (
    <Card header={<CardHeader subtitleText="" titleText="SMTP Setup" />} style={{ width: "100%", overflow: "hidden" }}>
      <Grid container spacing={2} padding={2}>
        {/* Left Side: SMTP Configuration */}
        <Grid item xs={12} sm={6}>
          <h3 style={{ display: 'flex', alignItems: 'center' }}>
            SMTP Configuration
            <Switch
              checked={smtpEnabled}
              onChange={(e) => toggleSmtpEnabled(e.target.checked)}
              text=""
              style={{ marginLeft: '16px' }} // Add some margin to space it from the heading
            />
          </h3>
          {smtpEnabled && (
            <>
              <TextField
              size="small"
                value={server}
                onChange={(e) => setServer(e.target.value)}
                label="SMTP Server"
                variant="outlined"
                fullWidth
                margin="normal"
                error={serverError}
                helperText={serverError ? "Server is required" : ""}
              />
              <TextField
              size="small"
                value={port}
                onChange={(e) => setPort(e.target.value)}
                label="Port"
                variant="outlined"
                fullWidth
                margin="normal"
                error={portError}
                helperText={portError ? "Port is required" : ""}
              />
               <CheckBox 
                checked={useTls} 
                onChange={(e) => {
                  setUseTls(true);
                  setUseSsl(false);
                }} 
                text="Use TLS" 
              />
              <CheckBox 
                checked={useSsl} 
                onChange={(e) => {
                  setUseSsl(true);
                  setUseTls(false);
                }} 
                text="Use SSL" 
              />
              <TextField
              size="small"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                label="Username"
                variant="outlined"
                fullWidth
                margin="normal"
                error={usernameError}
                helperText={serverError ? "Username is required" : ""}
              />
              <TextField
              size="small"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                label="Password"
                type="password"
                variant="outlined"
                fullWidth
                margin="normal"
                error={passwordError}
                helperText={serverError ? "Password is required" : ""}
              />
              <TextField
              size="small"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                label="From Name"
                variant="outlined"
                fullWidth
                margin="normal"
                error={fromNameError}
                helperText={fromNameError ? "Name is required" : ""}
              />
             
              <CheckBox checked={invoiceWorkflowNotifications} onChange={(e) => setInvoiceWorkflowNotifications(e.target.checked)} text="Invoice Workflow Notifications" />
              <CheckBox checked={expenseWorkflowNotifications} onChange={(e) => setExpenseWorkflowNotifications(e.target.checked)} text="Expense Workflow Notifications" />
              <CheckBox checked={userOnboardingNotifications} onChange={(e) => setUserOnboardingNotifications(e.target.checked)} text="User Onboarding Notifications" />
              <CheckBox checked={vendorCommunicationNotifications} onChange={(e) => setVendorCommunicationNotifications(e.target.checked)} text="Vendor Communication Notifications" />
            </>
          )}
        </Grid>
        {/* Right Side: Test Email Configuration */}
        <Grid item xs={12} sm={6}>
          <h3>Test Email Configuration</h3>
          {!isLoading && (
        <>
          <TextField
            value={testEmail || ""}
            onChange={(e) => setTestEmail(e.target.value)}
            label="To Email (for testing)"
            variant="outlined"
            fullWidth
            margin="normal"
            error={testEmailError}
            helperText={testEmailError ? "Email is required" : ""}
          />
          <TextField
            value={emailSubject || ""}
            onChange={(e) => setEmailSubject(e.target.value)}
            label="Email Subject"
            variant="outlined"
            fullWidth
            margin="normal"
            error={emailSubjectError}
            helperText={emailSubjectError ? "Subject is required" : ""}
          />
          <TextField
            value={emailMessage || ""}
            onChange={(e) => setEmailMessage(e.target.value)}
            label="Email Message"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            margin="normal"
            error={emailMessageError}
            helperText={emailMessageError ? "Message is required" : ""}
          />
          </>
          )}
          <ButtonOutlined onClick={testSmtpSettings} style={{ marginTop: '16px' }}>Test</ButtonOutlined>
          <ButtonContained onClick={saveSmtpSettings} style={{ marginTop: '16px', marginLeft: '8px' }}>Save</ButtonContained>
        </Grid>
      </Grid>
    </Card>
  );
};

export default SmtpCard;
